import clsx from 'clsx'
import { useQuestionnaireForms } from 'components/QuestionnaireRequestAutomated/hooks/useQuestionnaireForms'
import useRequesterContext from 'components/QuestionnaireRequestAutomated/hooks/useRequesterContext'
import { useSearchForms } from 'components/QuestionnaireRequestAutomated/hooks/useSearchForms'
import { useIntersectionObserver } from 'components/utility/hooks/useIntersectionObserver'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { STEP } from '..'
import { FormatFormFile } from 'components/QuestionnaireRequestAutomated/utilities/requester'

const InputField = ({ onChange = () => {} }) => {
  return (
    <div className="form-search-input">
      <span>
        <i className="form-search-input-icon fa-light fa-magnifying-glass"></i>
      </span>
      <input type="text" onChange={onChange} className="form-search-input-body" placeholder="search form name" />
    </div>
  )
}

const FormListItem = ({ name, onClick, selected }) => {
  return (
    <li
      onClick={onClick}
      aria-selected={selected}
      className="form-search-list-item dome-d-flex dome-gap12 dome-align-center dome-cursor-pointer"
    >
      <i className="fa-light fa-file-lines dome-color-dark-grey"></i>
      <span className="form-search-list-item-text dome-p2 dome-color-dark-grey">{name}</span>
      {/*TODO: Display pricing estimation*/}
      {/*<span className="dome-p2 dome-color-dark-grey">$xxx</span>*/}
    </li>
  )
}

const FormList = () => {
  const { formFile, onNextStep, setFormFile, company } = useRequesterContext()
  const [value, setValue] = useState('')
  const [page, setPage] = useState(1)
  const observerTarget = useRef(null)
  const { data: questionnaireForms, total: questionnaireFormsTotal } = useQuestionnaireForms({
    companyId: company?.id,
    page: page,
  })
  const { data: searchForms, total: searchFormsTotal } = useSearchForms({ query: value, companyId: company?.id })

  const handleObserver = useCallback(() => {
    setPage((pre) => pre + 1)
  }, [])

  useIntersectionObserver(observerTarget, handleObserver, [questionnaireFormsTotal])

  const currentForms = useMemo(
    () => (value ? searchForms : questionnaireForms),
    [value, searchForms, questionnaireForms]
  )
  const isEmpty = useMemo(
    () => (value ? searchFormsTotal === 0 : questionnaireFormsTotal === 0),
    [value, searchFormsTotal, questionnaireFormsTotal]
  )

  return (
    <div className="form-search-container dome-d-flex dome-flex-column dome-gap12">
      <InputField onChange={(e) => setValue(e.target.value)} />
      {isEmpty ? (
        <div style={{ padding: '0 2.4rem' }}>
          <span className="dome-p2 dome-color-dark-grey">
            No matching forms to view. Try searching a different name or{' '}
          </span>
          <button
            type="button"
            onClick={() => onNextStep(STEP.FORM_UPLOAD)}
            className="dome-btn dome-btn-link dome-btn-link-aqua dome-btn-link-chevron-right"
          >
            upload your own
          </button>
        </div>
      ) : (
        <div>
          <ul className="form-search-list dome-d-flex dome-flex-column dome-gap6">
            {currentForms?.map((item) => (
              <FormListItem
                key={item.id}
                name={item.title}
                selected={formFile?.file_url === item.file_url}
                onClick={() =>
                  setFormFile(FormatFormFile({ file: item, pdfUrl: item?.file_url, fileName: item?.title }))
                }
              />
            ))}
            {questionnaireFormsTotal > questionnaireForms.length && (
              <li key={`observerTarget-${page}`} ref={observerTarget}></li>
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

export default function FormSelection() {
  const { formFile, onNextStep, onBackStep } = useRequesterContext()

  return (
    <div className="form-selection">
      <div className="dome-d-flex dome-flex-column dome-gap12 dome-full-width">
        <div className="dome-d-flex dome-flex-column dome-gap12">
          <span className="dome-p1 dome-color-darkest-grey dome-text-w500">find your form</span>
          <span className="dome-p2 dome-color-med-grey">
            Preview + select forms by choosing a file name. Alternatively, upload with the button below.
          </span>
        </div>
        <FormList />
      </div>
      <div className="form-selection-bottom dome-d-flex dome-justify-between">
        <button
          type="button"
          onClick={() => onNextStep(STEP.FORM_UPLOAD)}
          className="dome-btn dome-btn-base dome-btn-grey-stroke"
        >
          upload a form
        </button>
        <button
          type="button"
          disabled={!formFile}
          onClick={() => onNextStep(STEP.FORM_REQUEST_INFO)}
          className={clsx('dome-btn dome-btn-base', formFile ? 'dome-btn-dark-green' : 'dome-btn-disabled')}
        >
          next
        </button>
      </div>
      <div className="form-selection-navigation">
        <button
          type="button"
          onClick={onBackStep}
          className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-chevron-left"
        >
          back
        </button>
      </div>
    </div>
  )
}
